import apiMiddleware from "../api/apiMiddleware";

export const normalizeEventId = (id) => {
  const parsed = Number(id);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Unwrap Laravel API body: { message, data, status: true }
 * Works whether the caller passed axios response or response.data.
 */
export const unwrapApiPayload = (payload) => {
  if (!payload) return null;

  // Axios response (HTTP status is a number, e.g. 200)
  if (
    typeof payload.status === "number" &&
    payload.data &&
    typeof payload.data === "object"
  ) {
    return unwrapApiPayload(payload.data);
  }

  // API success body
  if (payload.status === true && payload.data !== undefined) {
    return payload.data;
  }

  // Already unwrapped record / array
  if (payload.id !== undefined || Array.isArray(payload)) {
    return payload;
  }

  // Nested record without status flag
  if (payload.data?.id !== undefined) {
    return payload.data;
  }

  return null;
};

/** Unwrap API body, axios response, or legacy React Query cache shapes. */
export const extractEventRecord = (payload) => {
  const data = unwrapApiPayload(payload);
  if (!data || Array.isArray(data)) return null;
  return data.id !== undefined ? data : null;
};

export const extractCreatedEventId = (axiosResponse) => {
  const fromRecord = normalizeEventId(extractEventRecord(axiosResponse)?.id);
  if (fromRecord) return fromRecord;

  const fromBody = normalizeEventId(extractEventRecord(axiosResponse?.data)?.id);
  if (fromBody) return fromBody;

  const nested = axiosResponse?.data?.data?.id ?? axiosResponse?.data?.id;
  return normalizeEventId(nested);
};

export const extractApiList = (payload) => {
  const data = unwrapApiPayload(payload);
  return Array.isArray(data) ? data : [];
};

export const extractGalleryImages = extractApiList;

/** Parent-level general info state (matches GeneralInfo → AddEvent sync shape). */
export const mapEventRecordToGeneralInfo = (record) => {
  if (!record?.id) return null;

  const parseDuration = (duration) => {
    if (!duration) return { value: "", unit: "hours" };
    const parts = String(duration).trim().split(/\s+/);
    return { value: parts[0] || "", unit: parts[1] || "hours" };
  };

  const { value: durationValue, unit: durationUnit } = parseDuration(
    record.duration
  );

  return {
    id: record.id,
    is_approved: record.is_approved,
    is_draft: record.is_draft,
    event_category_id: record.event_category_id || "",
    name: record.name || "",
    starting_date: record.starting_date || "",
    ending_date: record.ending_date || "",
    latitude: record.latitude || "",
    longitude: record.longitude || "",
    address: record.address || "",
    description: record.description || "",
    free_booking: Boolean(record.free_booking),
    refundable: Boolean(record.refundable),
    age_limit:
      record.age_limit !== undefined && record.age_limit !== null
        ? String(record.age_limit)
        : "",
    duration: record.duration || "",
    duration_value: durationValue,
    duration_unit: durationUnit,
    chargable_from:
      record.chargable_from !== undefined && record.chargable_from !== null
        ? String(record.chargable_from)
        : "",
    layout: record.layout || "outdoor",
    seating_arrangement: record.seating_arrangement || "standing",
    kids_friendly: Boolean(record.kids_friendly),
    pets_friendly: Boolean(record.pets_friendly),
    language_ids: (record.languages || []).map((lang) => lang.id),
    current_stage: Number(record.current_stage) || 1,
    thumb_image: record.thumb_image || null,
    cover_image: record.cover_image || null,
    gallery_images: [],
    map_link: record.map_link || "",
    location:
      typeof record.location === "string"
        ? record.location
        : record.location?.address || "",
    google_maps_link: record.map_link || "",
    isUpdate: true,
  };
};

//*******************************************************************************
// EVENT LISTING & SEARCH SERVICES
//*******************************************************************************

export const list = async (page = 1, search = "", filter = "") => {
  let url = `/events-general-info?page=${page}&include=category`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  if (filter) url += `&category=${encodeURIComponent(filter)}`;
  const response = await apiMiddleware.get(url);

  console.log("response in event service", response);

  // The real array is in response.data.data.data
  const pagination = response.data?.data || {};
  return {
    data: pagination.data || [],
    current_page: pagination.current_page || 1,
    last_page: pagination.last_page || 1,
    total: pagination.total || 0,
    per_page: pagination.per_page || 10
  };
};

export const getEventById = async (id) => {
  return await apiMiddleware.get(`/events-general-info/${id}`);
};

export const changeEventName = async (id, name) => {
  return await apiMiddleware.put(`/events-general-info/${id}/name`, { name });
};

export const deleteEvent = async (id) => {
  return await apiMiddleware.delete(`/events-general-info/${id}`);
};

//*******************************************************************************
// GENERAL INFO TAB SERVICES
//*******************************************************************************

export const getEventCategories = async () => {
  return await apiMiddleware.get("/event-category-masters-options");
};

export const createEventCategory = async ({ name, image = null, slug = null }) => {
  const formData = new FormData();
  formData.append("name", name);
  formData.append("status", "1");
  if (slug) {
    formData.append("slug", slug);
  }
  if (image instanceof File) {
    formData.append("image", image);
  }
  const response = await apiMiddleware.post("/event-category-masters", formData);
  return response.data;
};

export const createLanguage = async ({ name }) => {
  const response = await apiMiddleware.post("language-master", {
    name,
    status: 1,
  });
  return response.data;
};

export const getEventTickets = async (eventId) => {
  const response = await apiMiddleware.get(`/events/${eventId}/ticket-types`);
  return response.data;
};

export const createEventTicket = async (eventId, data) => {
  const response = await apiMiddleware.post(`/events/${eventId}/ticket-types`, data);
  return response.data;
};

export const updateEventTicket = async (eventId, ticketId, data) => {
  const response = await apiMiddleware.put(`/events/${eventId}/ticket-types/${ticketId}`, data);
  return response.data;
};

export const deleteEventTicket = async (eventId, ticketId) => {
  const response = await apiMiddleware.delete(`/events/${eventId}/ticket-types/${ticketId}`);
  return response.data;
};

export const getLanguages = async () => {
  return await apiMiddleware.get("/language-masters-options");
};

export const createLanguageMaster = async ({ name }) => {
  const response = await apiMiddleware.post("/create-language-master", { name });
  return response.data;
};

export const createEvent = async (data) => {
  const response = await apiMiddleware.post("/events-general-info", data);
  return response;
};

export const updateEvent = async (id, data) => {
  return await apiMiddleware.post(`/events-general-info/${id}?_method=PUT`, data);
};

export const deleteEventImages = async (imageId) => {
  return await apiMiddleware.delete(`/events-general-info/images/${imageId}`);
};

export const getEventGallery = async (eventId) => {
  const response = await apiMiddleware.get(`/event-gallery/${eventId}`);
  return response.data;
};

export const deleteEventGalleryImage = async (eventId, imageId) => {
  return await apiMiddleware.delete(`/event-gallery/${eventId}/${imageId}`);
};

//*******************************************************************************
// PRICING TAB SERVICES
//*******************************************************************************

export const getEventDays = async (event_id) => {
  const response = await apiMiddleware.get(`/event-days/${event_id}`);
  return response.data.data;
};

export const getTicketTypesUnderEvent = async (event_id) => {
  const response = await apiMiddleware.get(`/event-ticket-types/${event_id}`);
  return response.data.data;
};

export const saveDay = async (data) => {  
  // Validate required fields
  if (!data.event_id || !data.event_day_id) {
    throw new Error("event_id and event_day_id are required");
  }

  // Structure the data according to API requirements
  const requestData = {
    event_id: data.event_id,
    event_day_id: data.event_day_id,
    shows: data.shows || [], // Array of {name, start_time, end_time}
    ticketPrice: data.ticketPrice || [] // Array of {price, available_slots, description, max_allowed_booking_per_user, discount, event_ticket_type_id}
  };

 

  const response = await apiMiddleware.post("/add-event-pricing", requestData);
  return response;
};


export const updateDay = async (data) => {
  
  // Format time fields to H:i format (24-hour format like "14:30")
  const formattedData = {
    ...data,
    shows: data.shows?.map(show => ({
      ...show,
      start_time: formatTimeToHi(show.start_time),
      end_time: formatTimeToHi(show.end_time)
    })) || []
  };

  console.log("Formatted update data:", formattedData);
  
  const response = await apiMiddleware.post(`/update-event-pricing?_method=PUT`, formattedData);
  return response;
};

// Helper function to format time to H:i format
const formatTimeToHi = (timeString) => {
  if (!timeString) return "";
  
  // If already in H:i format, return as is
  if (/^\d{1,2}:\d{2}$/.test(timeString)) {
    return timeString;
  }
  
  // If in H:i:s format, remove seconds
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(timeString)) {
    return timeString.substring(0, 5);
  }
  
  // If in 12-hour format (like "2:30 PM"), convert to 24-hour
  if (timeString.includes('AM') || timeString.includes('PM')) {
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours);
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    return `${hour24.toString().padStart(2, '0')}:${minutes}`;
  }
  
  // Default: return as is if format is unknown
  return timeString;
};

export const getEventPricing = async (eventId) => {
  console.log("entered into event pricing service", eventId);
  // Call with event_id parameter
  const response = await apiMiddleware.get(`/event-pricing/${eventId}`);
  console.log("response in event pricing service", response.data);
  return response.data.data;

};

export const advanceEventStage = async (eventId, currentStage) => {
  const response = await apiMiddleware.post("/event-pricing-save-and-continue", {
    event_id: eventId,
    current_stage: currentStage,
  });
  return response;
};

export const eventPricingContinue = async (data) => {
  return advanceEventStage(data.event_id, data.current_stage);
};

//*******************************************************************************
// HIGHLIGHTS TAB SERVICES
//*******************************************************************************


export const saveEventHighlights = async (data) => {
  const response = await apiMiddleware.post(`/event-highlights`, data);
  return response.data;
};


export const getEventHighlights = async (eventId) => {
  return await apiMiddleware.get(`/event-highlights/${eventId}`);
};


export const updateEventHighlights = async (data) => {
  const response = await apiMiddleware.post(
    `/event-highlights?_method=PUT`,
    data
  );
  return response.data;
};

export const deleteEventHighlights = async (highlight_id) => {
  const response = await apiMiddleware.delete(`/event-highlights/${highlight_id}`);
  return response.data;
};

//*******************************************************************************
// FAQ TAB SERVICES
//*******************************************************************************

export const getEventFAQ = async (eventId) => {
  const response = await apiMiddleware.get(`/event-faqs/${eventId}`);
  return response;
};

export const createEventFAQ = async (data) => {
  console.log("savefaq check");
  const response = await apiMiddleware.post(`/event-faqs`, data);
  return response;
};

export const updateEventFAQ = async (eventId, data) => {
  console.log("update faq check", eventId, data);
  const response = await apiMiddleware.post(`/event-faqs/${eventId}?_method=PUT`, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response;
};

export const deleteEventFAQ = async (eventId) => {
  const response = await apiMiddleware.delete(`/event-faqs/${eventId}`);
  return response;
};

// Alternative FAQ service names (used in some components)//
export const getEventFaq = async (eventId) => {
  const response = await apiMiddleware.get(`/event-faqs/${eventId}`);
  return response;
};

export const deleteEventFaq = async (faqId) => {
  const response = await apiMiddleware.delete(`/event-faqs/${faqId}`);
  return response;
};

//*******************************************************************************
// TERMS & CONDITIONS TAB SERVICES
//*******************************************************************************

export const getEventTermsAndConditions = async (eventId) => {
  const response = await apiMiddleware.get(
    `/event-terms-and-conditions-by-event/${eventId}`
  );
  return response.data;
};

export const saveEventTermsAndConditions = async (data) => {
  // Use the resource POST endpoint
  const response = await apiMiddleware.post('/event-terms-and-conditions', data);
  return response.data.data;
};

export const updateEventTermsAndConditions = async (eventId, data) => {
  const response = await apiMiddleware.post(
    `/event-terms-and-conditions/${eventId}?_method=PUT`,
    data
  );
  return response.data.data;
};

//*******************************************************************************
// CANCELLATION POLICY TAB SERVICES
//*******************************************************************************

export const getEventCancellationPolicy = async (eventId) => {
  const response = await apiMiddleware.get(`/events/cancellation-policies/${eventId}`);
  return response;
};

export const saveEventCancellationPolicy = async (data) => {
  const response = await apiMiddleware.post('/events/cancellation-policies', data);
  return response;
};

export const updateEventCancellationPolicy = async (eventId, data) => {
  const response = await apiMiddleware.post(`/events/cancellation-policies/${eventId}?_method=PUT`, data);
  return response;
};